/**
 * "Chính sách nhắn tin" — bản mô tả tiếng Việt cho trang Setting của admin.
 *
 * PHẢI KHỚP với server (hp-media-server-staging):
 *   src/chat-policy/chat-policy.constants.ts — danh sách chế độ, tên lớp, mã lỗi
 *   src/chat-policy/chat-policy.service.ts   — luật decideDmByClass /
 *                                              decidePostInGroup / decideCreateGroup
 * Server mới là nơi thực thi luật; bảng dưới đây chỉ để admin đọc hiểu trước
 * khi bấm lưu. Đổi luật bên server thì phải sửa lại chữ ở đây, và ngược lại
 * KHÔNG được thêm chế độ ở đây mà server chưa biết (DTO server @IsIn sẽ trả 400).
 */

export const CHAT_POLICY_MODE = {
  OPEN: 'OPEN',
  PUBLIC_MUTED: 'PUBLIC_MUTED',
  INTERNAL_FREE: 'INTERNAL_FREE',
  WORK_ONLY: 'WORK_ONLY',
} as const;

export type ChatPolicyMode = (typeof CHAT_POLICY_MODE)[keyof typeof CHAT_POLICY_MODE];

export const CHAT_POLICY_MODES: ChatPolicyMode[] = [
  CHAT_POLICY_MODE.OPEN,
  CHAT_POLICY_MODE.PUBLIC_MUTED,
  CHAT_POLICY_MODE.INTERNAL_FREE,
  CHAT_POLICY_MODE.WORK_ONLY,
];

// Giá trị lạ/undefined (chưa tải xong setting, DB cũ chưa có cột) → coi như
// OPEN, cùng cách server chuẩn hoá (normalizeChatPolicyMode).
export function isChatPolicyMode(raw: unknown): raw is ChatPolicyMode {
  return typeof raw === 'string' && (CHAT_POLICY_MODES as string[]).includes(raw);
}

// Nhãn trong Select — đúng câu chữ chủ doanh nghiệp đã chốt.
export const CHAT_POLICY_MODE_OPTIONS: { value: ChatPolicyMode; label: string }[] = [
  { value: CHAT_POLICY_MODE.OPEN, label: 'Như hiện nay (cần kết bạn, chưa phân loại)' },
  { value: CHAT_POLICY_MODE.PUBLIC_MUTED, label: 'User không được nhắn tin cho ai' },
  { value: CHAT_POLICY_MODE.INTERNAL_FREE, label: 'Nội bộ nhắn tự do, trừ User' },
  {
    value: CHAT_POLICY_MODE.WORK_ONLY,
    label:
      'Chỉ trao đổi công việc: Creator/Nhân viên nhắn Quản lý tổng, Creator trao đổi trong nhóm',
  },
];

// Lớp người dùng do server suy ra từ roles/isHpCompany/department (ChatClass).
export type ChatClass = 'MANAGEMENT' | 'AGENCY' | 'CREATOR' | 'STAFF' | 'PUBLIC';

export const CHAT_CLASS_LABEL_VI: Record<ChatClass, string> = {
  PUBLIC: 'User',
  STAFF: 'Nhân viên',
  CREATOR: 'Creator',
  AGENCY: 'Agency',
  MANAGEMENT: 'Quản lý tổng',
};

export const chatClassLabel = (cls: ChatClass | null | undefined): string =>
  cls && CHAT_CLASS_LABEL_VI[cls] ? CHAT_CLASS_LABEL_VI[cls] : '—';

export const CHAT_POLICY_LEGEND_VI =
  'User = tài khoản thường/fan (không có mã công ty, không có phòng ban) · Nhân viên = tài khoản thường có mã công ty hoặc có phòng ban (Makeup, Kỹ thuật, MC…) · Creator = tài khoản có vai trò CREATOR hoặc STUDIO_CREATOR (chỉ chọn bộ phận Creator mà chưa có vai trò này thì vẫn tính là Nhân viên) · Agency = tài khoản có vai trò AGENCY · Quản lý tổng = ADMIN và MOD (không bao giờ bị chặn)';

export const CHAT_POLICY_WARNING_VI =
  'Hội thoại cũ mà chế độ mới không cho phép sẽ chuyển sang chỉ đọc (không bị ẩn). Phòng voice không bị ảnh hưởng. Nhớ đặt "Vai trò được tạo nhóm chat" cho phù hợp.';

// Mã lỗi server trả về (ERROR_MESSAGE.CHAT_POLICY bên server) → câu giải thích.
export const CHAT_POLICY_CODE_LABEL_VI: Record<string, string> = {
  CHAT_POLICY_DM_NOT_ALLOWED: 'Không được mở hội thoại riêng mới',
  CHAT_POLICY_CONVERSATION_READ_ONLY: 'Hội thoại sẵn có chuyển sang chỉ đọc',
  CHAT_POLICY_GROUP_READ_ONLY: 'Chỉ đọc trong nhóm, không đăng được',
  CHAT_POLICY_CREATE_GROUP_DENIED: 'Không được tạo nhóm',
  CHAT_POLICY_GROUP_TYPE_NOT_ALLOWED: 'Không được tạo nhóm loại này',
};

// Kết quả GET /settings/check/chat-policy — khớp ChatPolicyCheckResult trong
// settings/setting.ts bên server. Các cờ là giá trị HIỆU LỰC (WORK_ONLY ép
// allGroupAnnouncementOnly), khác cột thô của GET /settings.
export type ChatPolicyCheckResult = {
  mode: string;
  class: ChatClass | null;
  canCreateGroup: boolean;
  dmAllowedClasses: ChatClass[];
  dmSameGroupUnlock: boolean;
  allGroupAnnouncementOnly: boolean;
  target?: {
    username: string;
    class: ChatClass | null;
    canDm: boolean;
    code?: string;
  };
};

// Một dòng của bảng ma trận: người gửi làm được gì ở chế độ đang chọn.
export type ChatPolicyPresetRow = {
  key: ChatClass;
  sender: string; // Người gửi
  dm: string; // Nhắn riêng cho ai
  groupPost: string; // Đăng trong nhóm
  createGroup: string; // Tạo nhóm
};

const row = (
  key: ChatClass,
  dm: string,
  groupPost: string,
  createGroup: string
): ChatPolicyPresetRow => ({ key, sender: CHAT_CLASS_LABEL_VI[key], dm, groupPost, createGroup });

// --- Mảnh dùng chung giữa các chế độ khác OPEN -------------------------------

// User: chặn ở mọi chế độ khác OPEN; chỉ được trả lời trong hội thoại admin
// đã ghép với Agency/Quản lý tổng (luật "grandfather" bên server).
const PUBLIC_DM_VI =
  'Không ai. Chỉ trả lời được trong hội thoại sẵn có với Agency/Quản lý tổng đã kết bạn (vd. do admin ghép), không mở hội thoại mới';
const PUBLIC_GROUP_VI = 'Chỉ đọc (trừ phòng voice)';
const PUBLIC_CREATE_VI = 'Không';

const MANAGEMENT_DM_VI = 'Bất kỳ ai, không cần kết bạn';
const MANAGEMENT_GROUP_VI = 'Mọi nhóm đang là thành viên, kể cả Thông báo chung (ALL)';
const MANAGEMENT_CREATE_VI = 'Luôn được';

// Nhóm nội bộ (NPC/Creator/Agency) ở PUBLIC_MUTED & INTERNAL_FREE.
const INTERNAL_GROUP_VI =
  'Mọi nhóm đang là thành viên; nhóm Thông báo chung (ALL) chỉ khi tắt "chỉ quản lý được đăng" hoặc là chủ nhóm';
// Nhóm nội bộ ở WORK_ONLY: server ép ALL chỉ đọc bất kể công tắc.
const WORK_ONLY_GROUP_VI =
  'Nhóm đang là thành viên (kênh trao đổi chính); nhóm Thông báo chung (ALL) chỉ đọc, trừ chủ nhóm';

const csvCreate = (roleText: string) =>
  `Nếu "Vai trò được tạo nhóm chat" trống hoặc có ${roleText}`;

// hasAnyRole bên server so khớp chuỗi con MỘT CHIỀU: roles của user có chứa tên
// role trong csv. Tài khoản STUDIO_CREATOR chứa 'CREATOR' nên qua khi csv có
// CREATOR; nhưng csv chỉ chọn STUDIO_CREATOR thì Creator thường ('CREATOR')
// không chứa chuỗi đó → không qua. Không viết "tính là" để admin khỏi hiểu là
// hai chiều.
const CREATOR_CSV_VI =
  'CREATOR (tài khoản STUDIO_CREATOR cũng qua; chọn riêng STUDIO_CREATOR thì Creator thường KHÔNG qua)';

const SAME_GROUP_HINT_VI =
  'Nhân viên/Creator/Agency cùng nhóm nếu bật "Cùng nhóm thì được nhắn riêng"';

/**
 * Ma trận theo chế độ. Nguồn sự thật là decideDmByClass / decidePostInGroup /
 * decideCreateGroup bên server — mỗi ô dưới đây là cách diễn đạt lại một nhánh
 * trong đó, không phải luật riêng của admin.
 */
export const CHAT_POLICY_PRESETS_VI: Record<ChatPolicyMode, ChatPolicyPresetRow[]> = {
  // OPEN: chưa phân loại, mọi đường code cũ chạy nguyên văn.
  OPEN: (['PUBLIC', 'STAFF', 'CREATOR', 'AGENCY', 'MANAGEMENT'] as ChatClass[]).map((key) =>
    row(
      key,
      'Bất kỳ ai đã kết bạn (chưa phân loại)',
      'Mọi nhóm đang là thành viên',
      'Theo "Vai trò được tạo nhóm chat" (app ẩn nút, server không chặn)'
    )
  ),

  PUBLIC_MUTED: [
    row('PUBLIC', PUBLIC_DM_VI, PUBLIC_GROUP_VI, PUBLIC_CREATE_VI),
    row(
      'STAFF',
      'Nhân viên, Creator, Agency, Quản lý tổng — phải đã kết bạn (như hiện nay). Không nhắn được User',
      INTERNAL_GROUP_VI,
      csvCreate('USER')
    ),
    row(
      'CREATOR',
      'Nhân viên, Creator, Agency, Quản lý tổng — phải đã kết bạn (như hiện nay). Không nhắn được User',
      INTERNAL_GROUP_VI,
      csvCreate(CREATOR_CSV_VI)
    ),
    row(
      'AGENCY',
      'Bất kỳ ai đã kết bạn, kể cả User (vd. Creator chưa duyệt do admin ghép)',
      INTERNAL_GROUP_VI,
      csvCreate('AGENCY')
    ),
    row('MANAGEMENT', MANAGEMENT_DM_VI, MANAGEMENT_GROUP_VI, MANAGEMENT_CREATE_VI),
  ],

  INTERNAL_FREE: [
    row('PUBLIC', PUBLIC_DM_VI, PUBLIC_GROUP_VI, PUBLIC_CREATE_VI),
    row(
      'STAFF',
      'Nhân viên, Creator, Agency, Quản lý tổng — không cần kết bạn. Không nhắn được User',
      INTERNAL_GROUP_VI,
      csvCreate('USER')
    ),
    row(
      'CREATOR',
      'Nhân viên, Creator, Agency, Quản lý tổng — không cần kết bạn. Không nhắn được User',
      INTERNAL_GROUP_VI,
      csvCreate(CREATOR_CSV_VI)
    ),
    row(
      'AGENCY',
      'Nhân viên, Creator, Agency, Quản lý tổng — không cần kết bạn; User chỉ khi đã kết bạn (vd. Creator chưa duyệt do admin ghép)',
      INTERNAL_GROUP_VI,
      csvCreate('AGENCY')
    ),
    row('MANAGEMENT', MANAGEMENT_DM_VI, MANAGEMENT_GROUP_VI, MANAGEMENT_CREATE_VI),
  ],

  WORK_ONLY: [
    row('PUBLIC', PUBLIC_DM_VI, PUBLIC_GROUP_VI, PUBLIC_CREATE_VI),
    row(
      'STAFF',
      `Quản lý tổng; Agency đã kết bạn; ${SAME_GROUP_HINT_VI}. Không nhắn được User`,
      WORK_ONLY_GROUP_VI,
      'Không'
    ),
    row(
      'CREATOR',
      `Quản lý tổng; Agency đã kết bạn (Ghép Creator); ${SAME_GROUP_HINT_VI}. Không nhắn được User`,
      WORK_ONLY_GROUP_VI,
      'Không'
    ),
    row(
      'AGENCY',
      `Quản lý tổng; bất kỳ ai đã kết bạn, kể cả User; ${SAME_GROUP_HINT_VI}`,
      WORK_ONLY_GROUP_VI,
      'Chỉ khi "Vai trò được tạo nhóm chat" có AGENCY'
    ),
    row('MANAGEMENT', MANAGEMENT_DM_VI, MANAGEMENT_GROUP_VI, MANAGEMENT_CREATE_VI),
  ],
};
